/**
 * ERGO 360 Bundle Section
 *
 * 功能：
 * 1. Tab 切换显示不同产品组合
 * 2. 复选框选择/取消产品
 * 3. 实时计算总价
 * 4. 批量添加到购物车
 */

class Ergo360Bundle {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    // 缓存 DOM 元素（使用 BEM 类名）
    this.tabs = container.querySelectorAll('.ergo-360-bundle__tab');
    this.panels = container.querySelectorAll('.ergo-360-bundle__panel');
    this.tabSlider = container.querySelector('.ergo-360-bundle__tab-slider');
    this.subtitles = container.querySelectorAll('[data-tab-subtitle]');
    this.totalElement = container.querySelector('[data-bundle-total]');
    this.submitButton = container.querySelector('[data-bundle-submit]');
    this.errorElement = container.querySelector('[data-bundle-error]');

    // 当前激活的 Tab
    this.activeTab = 1;

    // 初始化
    this.init();
  }

  init() {
    this.bindTabEvents();
    this.bindCheckboxEvents();
    this.bindSubmitEvent();

    // 初始化 slider 位置和尺寸
    this.initSlider();

    // 监听窗口 resize 更新 slider
    this.bindResizeEvent();

    // 初始计算总价
    this.calculateTotal();
  }

  /**
   * 初始化 slider 的尺寸和位置
   */
  initSlider() {
    if (!this.tabSlider || this.tabs.length === 0) return;

    // 找到当前激活的 tab
    const activeTab = this.container.querySelector('.ergo-360-bundle__tab.is-active');
    if (activeTab) {
      this.updateSliderPosition(activeTab);
    }
  }

  /**
   * 更新 slider 位置到指定的 tab
   */
  updateSliderPosition(tab) {
    if (!this.tabSlider || !tab) return;

    const tabRect = tab.getBoundingClientRect();
    const containerRect = tab.parentElement.getBoundingClientRect();

    // 计算相对位置（相对于容器，减去容器的 padding）
    const offsetLeft = tab.offsetLeft;
    const width = tabRect.width;

    this.tabSlider.style.width = `${width}px`;
    this.tabSlider.style.transform = `translateX(${offsetLeft - 4}px)`;
  }

  /**
   * 绑定窗口 resize 事件
   */
  bindResizeEvent() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const activeTab = this.container.querySelector('.ergo-360-bundle__tab.is-active');
        if (activeTab) {
          this.updateSliderPosition(activeTab);
        }
      }, 100);
    });
  }

  /**
   * 绑定 Tab 切换事件
   */
  bindTabEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const tabIndex = parseInt(tab.dataset.tab, 10);
        this.switchTab(tabIndex);
      });
    });
  }

  /**
   * 切换 Tab
   * @param {number} tabIndex - Tab 索引 (1, 2, 3)
   */
  switchTab(tabIndex) {
    if (tabIndex === this.activeTab) return;

    // GA4 埋点：Bundle Tab 切换
    if (window.Ergo360Analytics) {
      const tabNames = { 1: 'Basic', 2: 'Plus', 3: 'Premium' };
      window.Ergo360Analytics.bundleTabSwitch(tabNames[tabIndex] || `Tab${tabIndex}`);
    }

    // 更新 Tab 状态
    let newActiveTab = null;
    this.tabs.forEach((tab) => {
      const isActive = parseInt(tab.dataset.tab, 10) === tabIndex;
      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        newActiveTab = tab;
      }
    });

    // 移动 slider 到新的 tab
    if (newActiveTab) {
      this.updateSliderPosition(newActiveTab);
    }

    // 更新 Panel 状态
    this.panels.forEach((panel) => {
      const isActive = parseInt(panel.dataset.panel, 10) === tabIndex;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    // 更新副标题状态
    this.subtitles.forEach((subtitle) => {
      const isActive = parseInt(subtitle.dataset.tabSubtitle, 10) === tabIndex;
      subtitle.classList.toggle('is-active', isActive);
      subtitle.hidden = !isActive;
    });

    this.activeTab = tabIndex;

    // 重新计算总价
    this.calculateTotal();
  }

  /**
   * 绑定复选框事件
   */
  bindCheckboxEvents() {
    // 使用事件委托监听 checkbox change 事件
    this.container.addEventListener('change', (e) => {
      if (e.target.matches('[data-bundle-checkbox]')) {
        // GA4 埋点：Bundle 产品勾选/取消
        if (window.Ergo360Analytics) {
          const item = e.target.closest('.ergo-360-bundle__item');
          const productName = item?.querySelector('.ergo-360-bundle__item-name')?.textContent?.trim() || '';
          const action = e.target.checked ? 'select' : 'deselect';
          const tabNames = { 1: 'Basic', 2: 'Plus', 3: 'Premium' };
          window.Ergo360Analytics.bundleProductToggle(productName, action, tabNames[this.activeTab] || `Tab${this.activeTab}`);
        }
        this.calculateTotal();
      }
    });

    // 整行点击切换 checkbox
    this.container.addEventListener('click', (e) => {
      const item = e.target.closest('.ergo-360-bundle__item');
      if (!item) return;

      // 如果点击的是 checkbox 本身或 label，不做处理（让原生行为处理）
      if (e.target.matches('[data-bundle-checkbox]') || e.target.closest('.ergo-360-bundle__checkbox-wrap')) {
        return;
      }

      // 检查是否为必选产品或不可用产品
      const isRequired = item.dataset.required === 'true';
      const isAvailable = item.dataset.available === 'true';
      if (isRequired || !isAvailable) return;

      // 切换 checkbox 状态
      const checkbox = item.querySelector('[data-bundle-checkbox]');
      if (checkbox && !checkbox.disabled) {
        checkbox.checked = !checkbox.checked;
        // 手动触发 change 事件以更新总价
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * 计算总价
   */
  calculateTotal() {
    const activePanel = this.container.querySelector(`.ergo-360-bundle__panel[data-panel="${this.activeTab}"]`);
    if (!activePanel) {
      // 如果没有当前 tab 的 panel，尝试第一个可用的
      const firstPanel = this.container.querySelector('.ergo-360-bundle__panel');
      if (!firstPanel) return;
    }

    let total = 0;
    let selectedCount = 0;

    const panel = activePanel || this.container.querySelector('.ergo-360-bundle__panel.is-active');
    if (!panel) return;

    const items = panel.querySelectorAll('.ergo-360-bundle__item');
    items.forEach((item) => {
      const checkbox = item.querySelector('[data-bundle-checkbox]');
      const price = parseInt(item.dataset.price, 10) || 0;
      const isAvailable = item.dataset.available === 'true';

      if (checkbox && checkbox.checked && isAvailable) {
        total += price;
        selectedCount++;
      }
    });

    // 更新总价显示
    this.updateTotalDisplay(total);

    // 更新提交按钮状态
    this.submitButton.disabled = selectedCount === 0;
  }

  /**
   * 更新总价显示
   * @param {number} total - 总价（分）
   */
  updateTotalDisplay(total) {
    const formattedPrice = this.formatMoney(total);
    this.totalElement.textContent = formattedPrice;
  }

  /**
   * 格式化货币
   * @param {number} cents - 价格（分）
   * @returns {string} 格式化后的价格
   */
  formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.settings && theme.settings.moneyFormat) {
      const moneyFormat = theme.settings.moneyFormat;
      const value = (cents / 100).toFixed(2);
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
    }
    // 降级方案
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * 绑定提交事件
   */
  bindSubmitEvent() {
    this.submitButton.addEventListener('click', () => {
      this.addToCart();
    });
  }

  /**
   * 批量添加到购物车
   */
  async addToCart() {
    const activePanel = this.container.querySelector(`.ergo-360-bundle__panel[data-panel="${this.activeTab}"]`) ||
                        this.container.querySelector('.ergo-360-bundle__panel.is-active');
    if (!activePanel) return;

    // 收集选中的产品
    const items = [];
    const productItems = activePanel.querySelectorAll('.ergo-360-bundle__item');

    productItems.forEach((item) => {
      const checkbox = item.querySelector('[data-bundle-checkbox]');
      const variantId = item.dataset.variantId;
      const isAvailable = item.dataset.available === 'true';

      if (checkbox && checkbox.checked && isAvailable && variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: 1
        });
      }
    });

    if (items.length === 0) {
      this.showError('Please select at least one product.');
      return;
    }

    // 禁用按钮，显示加载状态
    this.submitButton.disabled = true;
    this.submitButton.setAttribute('aria-busy', 'true');
    this.hideError();

    try {
      // 检查是否在购物车页面
      if (document.body.classList.contains('template-cart') ||
          (typeof theme !== 'undefined' && theme.settings && theme.settings.cartType === 'page')) {
        // 使用表单提交方式
        this.submitViaForm(items);
        return;
      }

      // 收集需要更新的 section
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      // 发送请求
      const cartAddUrl = (typeof theme !== 'undefined' && theme.routes && theme.routes.cart_add_url)
        ? theme.routes.cart_add_url
        : '/cart/add.js';

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: items,
          sections: sectionsToBundle.length > 0 ? sectionsToBundle : undefined,
          sections_url: window.location.pathname
        })
      });

      const result = await response.json();

      if (result.status) {
        // 错误状态
        this.showError(result.description || 'Failed to add to cart.');
        return;
      }

      // GA4 埋点：Bundle 加入购物车
      if (window.Ergo360Analytics) {
        const tabNames = { 1: 'Basic', 2: 'Plus', 3: 'Premium' };
        const productNames = [];
        let totalPrice = 0;

        productItems.forEach((item) => {
          const checkbox = item.querySelector('[data-bundle-checkbox]');
          const isAvailable = item.dataset.available === 'true';
          if (checkbox && checkbox.checked && isAvailable) {
            const name = item.querySelector('.ergo-360-bundle__item-name')?.textContent?.trim() || '';
            productNames.push(name);
            totalPrice += parseInt(item.dataset.price, 10) || 0;
          }
        });

        window.Ergo360Analytics.bundleAddToCart(
          tabNames[this.activeTab] || `Tab${this.activeTab}`,
          productNames,
          totalPrice
        );
      }

      // 成功 - 获取最新购物车数据
      const cartUrl = (typeof theme !== 'undefined' && theme.routes && theme.routes.cart_url)
        ? theme.routes.cart_url
        : '/cart.js';

      const cartResponse = await fetch(cartUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      const cartJson = await cartResponse.json();

      if (result.sections) {
        cartJson['sections'] = result['sections'];
      }

      // 发布购物车更新事件
      if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.publish) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'ergo-360-bundle',
          cart: cartJson
        });
      }

      // 触发自定义事件
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: result
        }
      }));

      // 打开购物车 drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show(this.submitButton);
      }

    } catch (error) {
      this.showError('An error occurred. Please try again.');
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
      // 重新检查按钮状态
      this.calculateTotal();
    }
  }

  /**
   * 通过表单提交（用于购物车页面）
   * @param {Array} items - 产品项数组
   */
  submitViaForm(items) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/cart/add';

    items.forEach((item, index) => {
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = `items[${index}][id]`;
      idInput.value = item.id;
      form.appendChild(idInput);

      const qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = `items[${index}][quantity]`;
      qtyInput.value = item.quantity;
      form.appendChild(qtyInput);
    });

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * 显示错误信息
   * @param {string} message - 错误信息
   */
  showError(message) {
    if (this.errorElement) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }
  }

  /**
   * 隐藏错误信息
   */
  hideError() {
    if (this.errorElement) {
      this.errorElement.hidden = true;
    }
  }
}

// ============================================
// 初始化
// ============================================

function initErgo360Bundle() {
  const containers = document.querySelectorAll('.ergo-360-bundle');
  containers.forEach((container) => {
    // 避免重复初始化
    if (!container.dataset.initialized) {
      new Ergo360Bundle(container);
      container.dataset.initialized = 'true';
    }
  });
}

// DOM Ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initErgo360Bundle);
} else {
  initErgo360Bundle();
}

// Shopify Section Rendering API 支持
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.ergo-360-bundle');
    if (container) {
      container.dataset.initialized = '';
      new Ergo360Bundle(container);
      container.dataset.initialized = 'true';
    }
  });
}
